import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useResolveBadge } from '@/api/queries/virtual-badge-queries'
import type { BadgeScanMode } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

export interface ScannedPerson {
	id: string
	badge: string
	name: string
}

const RESOLVE_ERROR_KEYS: Record<string, string> = {
	BADGE_TOKEN_EXPIRED: 'dds.badgeTokenExpired',
	BADGE_TOKEN_INVALID: 'dds.badgeTokenInvalid',
	PERSON_NOT_FOUND: 'dds.badgeNotFound',
}

// Converte o QR lido no scanner do DDS na pessoa dona do crachá.
// Físico: matrícula pura, buscada na lista local. Virtual: token validado no backend.
export function useDdsBadgeScan(people: ScannedPerson[] | undefined) {
	const { t } = useTranslation()
	const resolveBadge = useResolveBadge()

	const findByBadge = (badge: string) =>
		people?.find((person) => person.badge === badge.trim().toUpperCase())

	const resolveScannedPerson = async (
		code: string,
		mode: BadgeScanMode,
	): Promise<ScannedPerson | null> => {
		if (mode === 'physical') {
			const person = findByBadge(code)
			if (!person) toast.error(t('dds.badgeNotFound'))
			return person ?? null
		}

		try {
			const resolved = await resolveBadge.mutateAsync(code)
			return (
				people?.find((person) => person.id === resolved.personId) ?? {
					id: resolved.personId,
					badge: resolved.badge,
					name: resolved.name,
				}
			)
		} catch (error) {
			const errorCode =
				error instanceof AxiosError
					? error.response?.data?.error_code
					: undefined
			const key = errorCode ? RESOLVE_ERROR_KEYS[errorCode] : undefined
			toast.error(key ? t(key) : getApiErrorMessage(error))
			return null
		}
	}

	return { resolveScannedPerson, isResolving: resolveBadge.isPending }
}
